import { Alert, Button, Stack, Typography, Link } from '@mui/material';
import { useProgressionModel } from 'features/wildfires/hooks/useProgressionModel';
import { Trans, useTranslation } from 'react-i18next';
import { getTimeAgo, SHORT_MONTH_DAY_12HOUR_FORMAT } from 'shared/dates';
import { formatDate } from 'date-fns';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { InlineStatus } from 'components/InlineStatus';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useProgressionModelsSources } from 'features/wildfires/hooks/useProgressionModelsSources';
import { ProgressionModelsList } from '../ProgressionModelsList';
import { ModelProviderAttribution } from '../ModelProviderAttribution';

type ProgressionModelDrawerContentProps = {
  geoEventId: number;
  modelId: number;
};

export const ProgressionModelDrawerContent = (
  props: ProgressionModelDrawerContentProps,
): JSX.Element | null => {
  const { geoEventId, modelId } = props;
  const { t } = useTranslation();
  const { geoEvent, isGeoEventLoading } = useGeoEvent<Wildfire>({
    id: geoEventId,
  });
  const { progressionModel } = useProgressionModel({ modelId });
  const { progressionModelsSources } = useProgressionModelsSources();
  const posthog = usePostHog();

  useEffect(() => {
    posthog.capture('viewed_progression_model', {
      geoEventId,
      progressionModelId: modelId,
      progressionModelsource: progressionModel?.sourceAttribution || 'unknown',
    });
  }, [geoEventId, modelId, posthog, progressionModel?.sourceAttribution]);

  if (isGeoEventLoading || !progressionModel) {
    return <InlineStatus status={isGeoEventLoading ? 'loading' : 'error'} />;
  }

  const providerSource = progressionModelsSources.find(
    (s) => s.sourceAttribution === progressionModel.sourceAttribution,
  )?.source;

  return (
    <>
      <Stack gap={2} sx={{ px: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
          {geoEvent?.name}
        </Typography>

        <Stack spacing={0.5}>
          <Typography>
            <Trans
              i18nKey="wildfireProgressionModel.updated"
              values={{
                timeAgo: getTimeAgo(
                  progressionModel.dateModified.toISOString(),
                ),
              }}
            >
              Updated <b>1 minute ago</b>
            </Trans>
          </Typography>

          <Typography variant="subtitle1" color="textSecondary">
            <Trans
              i18nKey="wildfireProgressionModel.createdBy"
              values={{
                name: progressionModel.sourceAttribution,
                timeAgo: getTimeAgo(progressionModel.dateCreated.toISOString()),
                date: formatDate(
                  new Date(progressionModel.dateCreated),
                  SHORT_MONTH_DAY_12HOUR_FORMAT,
                ),
              }}
            >
              Created by <b>Pyregence</b> • 5 days ago • Aug 4, 12:54 PM
            </Trans>
          </Typography>
        </Stack>

        <Alert severity="warning" icon={false}>
          <Stack spacing={1}>
            <Typography variant="body2" color="textPrimary">
              {t('wildfireProgressionModel.warning')}
            </Typography>
            <Button
              component={Link}
              size="small"
              variant="text"
              href="https://support.watchduty.org/hc/en-us/articles/38649553740685"
              target="_blank"
              sx={{
                color: 'text.primary',
                textDecoration: 'underline',
                width: 'fit-content',
                fontWeight: 'fontWeightBold',
                ml: '-4px !important',
              }}
            >
              {t('wildfireProgressionModel.learnMore')}
            </Button>
          </Stack>
        </Alert>
      </Stack>

      {providerSource && (
        <ModelProviderAttribution
          source={providerSource}
          geoEventId={geoEventId}
        />
      )}

      <ProgressionModelsList
        wildfireId={geoEventId}
        title={
          <Typography variant="h3">
            {t('wildfireProgressionModel.additionalModels')}
          </Typography>
        }
        excludeModel={progressionModel.sourceAttribution}
      />
    </>
  );
};
